/**
 * Multilingual spoken-alarm phrase dictionary — 8 languages × 4 levels × 9 gases.
 *
 * 협약(과제 협약서) 원문이 요구하는 음성경보 언어는 「한/영/베/몽/중/일 등 6개국어」다
 * (한국어 · English · Tiếng Việt · Монгол · 中文 · 日本語).
 * 2026-08-31 사용자 결정으로 여기에 현장 체류 인원이 많은 태국어·인도네시아어를
 * 더해 **협약 6개 + 태국어 + 인도네시아어 = 8개국어**로 확정했다.
 *
 *   협약 6종  ko (한국어) · en (English) · vi (Tiếng Việt) · mn (Монгол) · zh (中文 简体) · ja (日本語)
 *   추가 2종  th (ไทย) · id (Bahasa Indonesia)
 *
 * 배열 순서(`ALERT_LANGUAGES`)는 기존 저장값(localStorage `expertgas.tts.v4`)과의
 * 호환을 위해 기존 6종 순서를 유지한 뒤 mn · ja 를 뒤에 덧붙인 것이다.
 *
 * The 9 canonical gases mirror the backend field taxonomy
 * (`core/management/commands/seed_field.py :: NODE_GASES`):
 *   O2 · CO · H2S · CH4 · CO2 · NH3 · SO2 · NO2 · VOC
 * plus two commonly-reported extras (LEL combustible, SMOKE) for completeness.
 *
 * `buildAlertPhrase()` composes a full spoken sentence from
 * (level + gas + optional site) — e.g.
 *   ko  «심각 경보. 3동 황화수소 누출. 즉시 가까운 비상구로 대피하십시오.»
 *   en  «Critical alert. H2S leak at Bldg 3. Evacuate to the nearest emergency exit immediately.»
 * Grammar/word-order differs per language, so each language owns a small
 * `compose()` rather than a shared placeholder template.
 *
 * These are the strings the Web Speech API (and the edge-tts server fallback)
 * read aloud. `SPEECH_LOCALE` maps each app language to its BCP-47 speech tag.
 */

export const ALERT_LANGUAGES = ["ko", "en", "zh", "vi", "th", "id", "mn", "ja"] as const;
export type AlertLang = (typeof ALERT_LANGUAGES)[number];

export type Level = 0 | 1 | 2 | 3;

/** App language → BCP-47 tag for speechSynthesis + edge-tts voice lookup. */
export const SPEECH_LOCALE: Record<AlertLang, string> = {
  ko: "ko-KR",
  en: "en-US",
  zh: "zh-CN",
  vi: "vi-VN",
  th: "th-TH",
  id: "id-ID",
  mn: "mn-MN",
  ja: "ja-JP",
};

/** Native-script label for each language (used by the manual selector). */
export const LANG_LABEL: Record<AlertLang, string> = {
  ko: "한국어",
  en: "English",
  zh: "中文（简体）",
  vi: "Tiếng Việt",
  th: "ไทย",
  id: "Bahasa Indonesia",
  mn: "Монгол",
  ja: "日本語",
};

/** Short level word (정상/주의/경계/심각) per language, indexed by level 0..3. */
export const LEVEL_WORD: Record<AlertLang, [string, string, string, string]> = {
  ko: ["정상", "주의", "경계", "심각"],
  en: ["Normal", "Caution", "Warning", "Critical"],
  zh: ["正常", "注意", "警戒", "严重"],
  vi: ["Bình thường", "Chú ý", "Cảnh giác", "Nghiêm trọng"],
  th: ["ปกติ", "ระวัง", "เตือนภัย", "วิกฤต"],
  id: ["Normal", "Perhatian", "Waspada", "Kritis"],
  mn: ["Хэвийн", "Анхааруулга", "Сэрэмжлүүлэг", "Ноцтой"],
  ja: ["正常", "注意", "警戒", "重大"],
};

/**
 * Gas display names per language, keyed by canonical uppercase code.
 * `_multi` is the fallback when the gas is unknown / a composite alert.
 */
const GAS_NAME: Record<AlertLang, Record<string, string>> = {
  ko: {
    O2: "산소", CO: "일산화탄소", H2S: "황화수소", CH4: "메탄", CO2: "이산화탄소",
    NH3: "암모니아", SO2: "이산화황", NO2: "이산화질소", VOC: "휘발성 유기화합물",
    LEL: "가연성 가스", SMOKE: "연기", _multi: "복합 가스",
  },
  en: {
    O2: "oxygen", CO: "carbon monoxide", H2S: "hydrogen sulfide", CH4: "methane",
    CO2: "carbon dioxide", NH3: "ammonia", SO2: "sulfur dioxide",
    NO2: "nitrogen dioxide", VOC: "volatile organic compounds",
    LEL: "combustible gas", SMOKE: "smoke", _multi: "multiple gases",
  },
  zh: {
    O2: "氧气", CO: "一氧化碳", H2S: "硫化氢", CH4: "甲烷", CO2: "二氧化碳",
    NH3: "氨气", SO2: "二氧化硫", NO2: "二氧化氮", VOC: "挥发性有机物",
    LEL: "可燃气体", SMOKE: "烟雾", _multi: "复合气体",
  },
  vi: {
    O2: "ôxy", CO: "cacbon monoxit", H2S: "hydro sunfua", CH4: "mêtan",
    CO2: "cacbon điôxit", NH3: "amoniac", SO2: "lưu huỳnh điôxit",
    NO2: "nitơ điôxit", VOC: "hợp chất hữu cơ dễ bay hơi",
    LEL: "khí dễ cháy", SMOKE: "khói", _multi: "nhiều loại khí",
  },
  th: {
    O2: "ออกซิเจน", CO: "คาร์บอนมอนอกไซด์", H2S: "ไฮโดรเจนซัลไฟด์", CH4: "มีเทน",
    CO2: "คาร์บอนไดออกไซด์", NH3: "แอมโมเนีย", SO2: "ซัลเฟอร์ไดออกไซด์",
    NO2: "ไนโตรเจนไดออกไซด์", VOC: "สารอินทรีย์ระเหย",
    LEL: "ก๊าซไวไฟ", SMOKE: "ควัน", _multi: "ก๊าซหลายชนิด",
  },
  id: {
    O2: "oksigen", CO: "karbon monoksida", H2S: "hidrogen sulfida", CH4: "metana",
    CO2: "karbon dioksida", NH3: "amonia", SO2: "sulfur dioksida",
    NO2: "nitrogen dioksida", VOC: "senyawa organik volatil",
    LEL: "gas mudah terbakar", SMOKE: "asap", _multi: "beberapa gas",
  },
  mn: {
    O2: "хүчилтөрөгч", CO: "нүүрстөрөгчийн дутуу исэл", H2S: "хүхэрт устөрөгч",
    CH4: "метан", CO2: "нүүрстөрөгчийн давхар исэл", NH3: "аммиак",
    SO2: "хүхрийн давхар исэл", NO2: "азотын давхар исэл",
    VOC: "дэгдэмхий органик нэгдэл",
    LEL: "шатамхай хий", SMOKE: "утаа", _multi: "холимог хий",
  },
  ja: {
    O2: "酸素", CO: "一酸化炭素", H2S: "硫化水素", CH4: "メタン", CO2: "二酸化炭素",
    NH3: "アンモニア", SO2: "二酸化硫黄", NO2: "二酸化窒素", VOC: "揮発性有機化合物",
    LEL: "可燃性ガス", SMOKE: "煙", _multi: "複合ガス",
  },
};

/** Human gas name in a language, falling back to the raw code / composite term. */
export function gasDisplayName(lang: AlertLang, gas?: string | null): string {
  const table = GAS_NAME[lang] ?? GAS_NAME.ko;
  if (!gas) return table._multi;
  const code = gas.trim().toUpperCase();
  return table[code] ?? gas; // unknown code → speak it verbatim
}

/** Short level label ("심각" / "Critical" / …). */
export function levelLabel(lang: AlertLang, level: Level): string {
  return (LEVEL_WORD[lang] ?? LEVEL_WORD.ko)[level] ?? "";
}

// ── Per-language sentence composition ────────────────────────────────────────
// Each returns a complete utterance for (level, gas-name, optional site name).
type Compose = (level: Level, gas: string, site?: string) => string;

const COMPOSE: Record<AlertLang, Compose> = {
  ko: (level, gas, site) => {
    const loc = site ? `${site} ` : "";
    switch (level) {
      case 3: return `심각 경보. ${loc}${gas} 누출. 즉시 가까운 비상구로 대피하십시오.`;
      case 2: return `경계 경보. ${loc}${gas} 감지. 작업을 멈추고 환기하십시오.`;
      case 1: return `주의. ${loc}${gas} 농도가 상승했습니다. 주변을 확인하십시오.`;
      default: return `정상. ${loc}${gas} 농도는 정상 범위입니다.`;
    }
  },
  en: (level, gas, site) => {
    const loc = site ? ` at ${site}` : "";
    switch (level) {
      case 3: return `Critical alert. ${gas} leak${loc}. Evacuate to the nearest emergency exit immediately.`;
      case 2: return `Warning. ${gas} detected${loc}. Stop work and ventilate the area.`;
      case 1: return `Caution. Rising ${gas} level${loc}. Check your surroundings.`;
      default: return `Normal. ${gas} level${loc} is within the normal range.`;
    }
  },
  zh: (level, gas, site) => {
    const loc = site ? `在${site}，` : "";
    switch (level) {
      case 3: return `${loc}严重警报。检测到${gas}泄漏。请立即前往最近的紧急出口疏散。`;
      case 2: return `${loc}警戒。检测到${gas}。请停止作业并通风。`;
      case 1: return `${loc}注意。${gas}浓度上升。请检查周围环境。`;
      default: return `${loc}正常。${gas}浓度处于正常范围。`;
    }
  },
  vi: (level, gas, site) => {
    const loc = site ? ` tại ${site}` : "";
    switch (level) {
      case 3: return `Cảnh báo nghiêm trọng. Rò rỉ ${gas}${loc}. Sơ tán ngay đến lối thoát hiểm gần nhất.`;
      case 2: return `Cảnh giác. Phát hiện ${gas}${loc}. Ngừng công việc và thông gió khu vực.`;
      case 1: return `Chú ý. Nồng độ ${gas} đang tăng${loc}. Kiểm tra xung quanh.`;
      default: return `Bình thường. Nồng độ ${gas}${loc} trong ngưỡng an toàn.`;
    }
  },
  th: (level, gas, site) => {
    const loc = site ? `ที่ ${site} ` : "";
    switch (level) {
      case 3: return `แจ้งเตือนขั้นวิกฤต ${loc}พบการรั่วไหลของ${gas} อพยพไปยังทางออกฉุกเฉินที่ใกล้ที่สุดทันที`;
      case 2: return `เตือนภัย ${loc}ตรวจพบ${gas} หยุดการทำงานและระบายอากาศ`;
      case 1: return `ระวัง ${loc}ระดับ${gas}กำลังสูงขึ้น ตรวจสอบบริเวณโดยรอบ`;
      default: return `ปกติ ${loc}ระดับ${gas}อยู่ในเกณฑ์ปกติ`;
    }
  },
  id: (level, gas, site) => {
    const loc = site ? ` di ${site}` : "";
    switch (level) {
      case 3: return `Peringatan kritis. Kebocoran ${gas}${loc}. Segera evakuasi ke pintu darurat terdekat.`;
      case 2: return `Waspada. ${gas} terdeteksi${loc}. Hentikan pekerjaan dan lakukan ventilasi.`;
      case 1: return `Perhatian. Kadar ${gas} meningkat${loc}. Periksa sekitar Anda.`;
      default: return `Normal. Kadar ${gas}${loc} dalam rentang aman.`;
    }
  },
  // 몽골어: 장소는 여격 어미 「-д」로 붙이고 서술어는 문장 끝(SOV).
  // 대피 지시는 통보체(「…дохио.」)가 아니라 감탄·명령형이라야 긴급도가 전달된다.
  mn: (level, gas, site) => {
    const loc = site ? `${site}-д ` : "";
    switch (level) {
      case 3: return `Ноцтой аюул! ${loc}${gas} алдагдал илэрлээ. Яаралтай хамгийн ойрын онцгой байдлын гарцаар гадагш гарна уу.`;
      case 2: return `Сэрэмжлүүлэг. ${loc}${gas} илэрлээ. Ажлаа зогсоож, агааржуулалтаа шалгана уу.`;
      case 1: return `Анхааруулга. ${loc}${gas}-ын хэмжээ нэмэгдэж байна. Эргэн тойрноо шалгана уу.`;
      default: return `Хэвийн. ${loc}${gas}-ын хэмжээ хэвийн байна.`;
    }
  },
  // 일본어: 장소는 「〜で」, 지시는 방재방송 표준형 「直ちに…してください」.
  // 避難(ひなん)/待機(たいき)는 음이 전혀 달라 한국어식 「대피/대기」 혼동이 없다.
  ja: (level, gas, site) => {
    const loc = site ? `${site}で` : "";
    switch (level) {
      case 3: return `重大警報。${loc}${gas}の漏えいを検知しました。直ちに最寄りの非常口へ避難してください。`;
      case 2: return `警戒警報。${loc}${gas}を検知しました。作業を中止し、換気してください。`;
      case 1: return `注意。${loc}${gas}の濃度が上昇しています。周囲を確認してください。`;
      default: return `正常。${loc}${gas}の濃度は正常範囲です。`;
    }
  },
};

export interface AlertPhraseInput {
  lang: AlertLang;
  level: Level;
  /** Canonical gas code (e.g. "H2S") or a name; empty → composite phrasing. */
  gas?: string | null | undefined;
  /** Optional site/location clause (already localised name). */
  site?: string | null | undefined;
}

/**
 * Build the full spoken sentence for an alert in the given language.
 * Safe for any input: unknown language → ko, unknown gas → spoken verbatim.
 */
export function buildAlertPhrase({ lang, level, gas, site }: AlertPhraseInput): string {
  const l: AlertLang = ALERT_LANGUAGES.includes(lang) ? lang : "ko";
  const compose = COMPOSE[l];
  const gasName = gasDisplayName(l, gas);
  return compose(level, gasName, site ?? undefined);
}

/** Normalise any i18n language string ("ko-KR", "zh-CN", "zh") to an AlertLang. */
export function toAlertLang(lng?: string | null): AlertLang {
  const base = (lng ?? "ko").toLowerCase().split("-")[0];
  return (ALERT_LANGUAGES as readonly string[]).includes(base) ? (base as AlertLang) : "ko";
}

// ── 경보 종류별 문장 (2026-09-28) ─────────────────────────────────────────────
// 서버 경보에는 종류(kind)가 있다: gas(가스 위험) · health(설비 무응답 — 현장 PC·센서·현장 수신 끊김).
// ⛔ 예전엔 종류를 보지 않고 가스 문장만 만들었다 → gas 가 빈 무응답 경보(등급 3)가
//    8개 언어 모두 「심각 경보. ○○ 복합 가스 누출. 즉시 대피하십시오」로 방송됐다(09-28 16:21 금산
//    「매니져 12분 무응답」). 무응답은 가스 위험이 아니라 「현장 경보가 안 울릴 수 있다」는 설비 경고다 —
//    대피 지시를 내면 안 된다. 훈련 경보는 앞에 「훈련 상황입니다」를 붙인다.

export type AlertKindLike = "gas" | "health" | string;
/**
 * 설비 경보 대상. manager·node·site 는 무응답(ops 스캔), sensor 는 **센서 판독 모순**(충돌계수 K — 서버
 * safety/risk_engine/sensor_health.py, 「sensor:{node}:{gas}」, SENSOR_HEALTH_K_ALERT 기본 끔)이다. sensor 는 응답이
 * 없는 게 아니라 판독이 튀는 것이라 무응답 문장으로 읽으면 안 된다(10-20 3차 재통합 B1b×F1 — 예전엔 site 로 떨어져
 * 「현장 장비 응답이 없습니다」로 나왔다).
 */
export type HealthSubject = "manager" | "node" | "site" | "sensor" | "lamp";

/**
 * subject_key("manager:16" · "node:31" · "site:14" · "sensor:61:CO" · "lamp:16") → 무엇이 응답하지 않는지/점검이 필요한지.
 * lamp = 현장 PC 경광등 끊김(서버 ops 10분 점검, 2026-11-04). 무응답이 아니다 — 매니져는 살아서 판정·방송한다.
 */
export function healthSubject(subjectKey?: string | null): HealthSubject {
  const k = (subjectKey ?? "").split(":")[0];
  return k === "manager" || k === "node" || k === "sensor" || k === "lamp" ? k : "site";
}

/**
 * 관제 화면이 소리 내어 읽지 않는 경보인가 — 경광등 끊김(경고 2). 2026-11-04 사용자 결정 「방송 안 함」:
 * 가스 위험이 아니라 장비 점검 알림이라 화면·OS 알림 글로만 알린다(AlertTtsAnnouncer 가 낭독 후보에서 뺀다).
 */
export function isSilentHealth(kind?: AlertKindLike | null, subjectKey?: string | null): boolean {
  return kind === "health" && healthSubject(subjectKey) === "lamp";
}

/** 「sensor:{node}:{gas}」 의 가스 코드(없으면 빈 문자열). */
function sensorGasCode(subjectKey?: string | null): string {
  const parts = (subjectKey ?? "").split(":");
  return parts[0] === "sensor" ? parts.slice(2).join(":").trim() : "";
}

// ── 센서 점검 필요(판독 모순 K) — ko·en 만 둔다 ─────────────────────────────────────
// 나머지 6개 언어는 화면 i18n 의 누락 정책(STRICT = ko·en, 없으면 en 으로 대신)과 같게 en 문장으로 대신한다 —
// 번역 검수 전 문장을 방송에 싣지 않는다. 이 경보는 주의(1) 등급이라 낭독 대상이 아니고(알림음도 없음, useAlertTTS
// chimesFor) 화면·OS 알림 글로만 쓰인다.
type SensorCheckCompose = (gas: string, site?: string) => string;
const SENSOR_CHECK_COMPOSE: { en: SensorCheckCompose } & Partial<Record<AlertLang, SensorCheckCompose>> = {
  ko: (gas, site) =>
    `설비 점검. ${site ? `${site} ` : ""}${gas ? `${gas} ` : ""}센서 판독이 앞뒤·주변과 크게 어긋납니다. 센서 점검이 필요합니다.`,
  en: (gas, site) =>
    `Equipment check. The ${gas ? `${gas} ` : ""}sensor${site ? ` at ${site}` : ""} is giving inconsistent readings. Sensor check required.`,
};
/** 화면·OS 알림 제목의 짧은 말(가스 이름 자리) — 위와 같은 언어 정책. */
const SENSOR_CHECK_WORD: { en: string } & Partial<Record<AlertLang, string>> = {
  ko: "센서 점검 필요",
  en: "Sensor check required",
};

function composeSensorCheck(l: AlertLang, subjectKey?: string | null, site?: string): string {
  const own = SENSOR_CHECK_COMPOSE[l];
  const fl: AlertLang = own ? l : "en";
  const code = sensorGasCode(subjectKey);
  return (own ?? SENSOR_CHECK_COMPOSE.en)(code ? gasDisplayName(fl, code) : "", site);
}

// ── 경광등 끊김(lamp) — ko·en 만 둔다(센서 점검과 같은 언어 정책: 나머지는 en 으로 대신) ──────────────────
// 낭독하지 않는다(isSilentHealth) — 화면·OS 알림 글로만 쓰인다.
type LampCompose = (site?: string) => string;
const LAMP_COMPOSE: { en: LampCompose } & Partial<Record<AlertLang, LampCompose>> = {
  ko: (site) =>
    `설비 점검. ${site ? `${site} ` : ""}현장 PC 경광등이 연결되어 있지 않습니다. 가스 감시와 방송은 정상입니다. 경광등 USB 선과 전원을 확인해 주십시오.`,
  en: (site) =>
    `Equipment check. The warning light on the site PC${site ? ` at ${site}` : ""} is disconnected. Gas monitoring and announcements are normal. Please check the light's USB cable and power.`,
};
const LAMP_WORD: { en: string } & Partial<Record<AlertLang, string>> = {
  ko: "경광등 끊김",
  en: "Warning light disconnected",
};

/** 화면·OS 알림 제목에 쓰는 짧은 말(가스 이름 자리). */
export const HEALTH_WORD: Record<AlertLang, string> = {
  ko: "설비 무응답",
  en: "Equipment not responding",
  zh: "设备无响应",
  vi: "Thiết bị không phản hồi",
  th: "อุปกรณ์ไม่ตอบสนอง",
  id: "Peralatan tidak merespons",
  mn: "Төхөөрөмж хариу өгөхгүй",
  ja: "設備無応答",
};

/** 훈련 경보 앞말. */
export const DRILL_PREFIX: Record<AlertLang, string> = {
  ko: "훈련 상황입니다.",
  en: "This is a drill.",
  zh: "这是演习。",
  vi: "Đây là diễn tập.",
  th: "นี่คือการฝึกซ้อม",
  id: "Ini adalah latihan.",
  mn: "Энэ бол сургуулилалт.",
  ja: "これは訓練です。",
};

type HealthCompose = (subject: Exclude<HealthSubject, "sensor" | "lamp">, site?: string) => string;
const HEALTH_COMPOSE: Record<AlertLang, HealthCompose> = {
  ko: (s, site) => {
    const loc = site ? `${site} ` : "";
    if (s === "manager") return `설비 경고. ${loc}현장 PC 응답이 없습니다. 현장 경보가 울리지 않을 수 있습니다. 현장을 확인해 주십시오.`;
    if (s === "node") return `설비 경고. ${loc}센서 응답이 없습니다. 측정이 멈췄을 수 있습니다. 현장을 확인해 주십시오.`;
    return `설비 경고. ${loc}현장 장비 응답이 없습니다. 현장을 확인해 주십시오.`;
  },
  en: (s, site) => {
    const at = site ? ` at ${site}` : "";
    if (s === "manager") return `Equipment alert. The site PC${at} is not responding. Local alarms may not sound. Please check the site.`;
    if (s === "node") return `Equipment alert. A sensor${at} is not responding. Measurements may have stopped. Please check the site.`;
    return `Equipment alert. Equipment${at} is not responding. Please check the site.`;
  },
  zh: (s, site) => {
    const loc = site ?? "";
    if (s === "manager") return `设备警告。${loc}现场电脑无响应。现场警报可能无法发出，请检查现场。`;
    if (s === "node") return `设备警告。${loc}传感器无响应。测量可能已停止，请检查现场。`;
    return `设备警告。${loc}现场设备无响应，请检查现场。`;
  },
  vi: (s, site) => {
    const at = site ? ` tại ${site}` : "";
    if (s === "manager") return `Cảnh báo thiết bị. Máy tính hiện trường${at} không phản hồi. Cảnh báo tại chỗ có thể không phát ra. Vui lòng kiểm tra hiện trường.`;
    if (s === "node") return `Cảnh báo thiết bị. Cảm biến${at} không phản hồi. Việc đo có thể đã dừng. Vui lòng kiểm tra hiện trường.`;
    return `Cảnh báo thiết bị. Thiết bị${at} không phản hồi. Vui lòng kiểm tra hiện trường.`;
  },
  th: (s, site) => {
    const at = site ? `ที่${site}` : "";
    if (s === "manager") return `แจ้งเตือนอุปกรณ์ คอมพิวเตอร์หน้างาน${at}ไม่ตอบสนอง สัญญาณเตือนในพื้นที่อาจไม่ดัง โปรดตรวจสอบหน้างาน`;
    if (s === "node") return `แจ้งเตือนอุปกรณ์ เซ็นเซอร์${at}ไม่ตอบสนอง การตรวจวัดอาจหยุดทำงาน โปรดตรวจสอบหน้างาน`;
    return `แจ้งเตือนอุปกรณ์ อุปกรณ์${at}ไม่ตอบสนอง โปรดตรวจสอบหน้างาน`;
  },
  id: (s, site) => {
    const at = site ? ` di ${site}` : "";
    if (s === "manager") return `Peringatan peralatan. PC lapangan${at} tidak merespons. Alarm setempat mungkin tidak berbunyi. Harap periksa lokasi.`;
    if (s === "node") return `Peringatan peralatan. Sensor${at} tidak merespons. Pengukuran mungkin terhenti. Harap periksa lokasi.`;
    return `Peringatan peralatan. Peralatan${at} tidak merespons. Harap periksa lokasi.`;
  },
  mn: (s, site) => {
    const loc = site ? `${site}-ийн ` : "";
    if (s === "manager") return `Тоног төхөөрөмжийн сэрэмжлүүлэг. ${loc}талбайн компьютер хариу өгөхгүй байна. Талбайн дохиолол дуугарахгүй байж магадгүй. Талбайг шалгана уу.`;
    if (s === "node") return `Тоног төхөөрөмжийн сэрэмжлүүлэг. ${loc}мэдрэгч хариу өгөхгүй байна. Хэмжилт зогссон байж магадгүй. Талбайг шалгана уу.`;
    return `Тоног төхөөрөмжийн сэрэмжлүүлэг. ${loc}төхөөрөмж хариу өгөхгүй байна. Талбайг шалгана уу.`;
  },
  ja: (s, site) => {
    const loc = site ? `${site}の` : "";
    if (s === "manager") return `設備警告。${loc}現場PCが応答していません。現場の警報が鳴らない可能性があります。現場を確認してください。`;
    if (s === "node") return `設備警告。${loc}センサーが応答していません。測定が止まっている可能性があります。現場を確認してください。`;
    return `設備警告。${loc}現場設備が応答していません。現場を確認してください。`;
  },
};

export interface SpokenAlertInput extends AlertPhraseInput {
  /** 서버 Alert.kind — "health" 면 설비 무응답 문장(대피 지시 없음). 없으면 가스. */
  kind?: AlertKindLike | null | undefined;
  /** 서버 Alert.is_drill — 참이면 앞에 「훈련 상황입니다」. */
  isDrill?: boolean | null | undefined;
  /** 서버 Alert.subject_key — 무응답 대상(manager/node/site). */
  subjectKey?: string | null | undefined;
}

// ── 누출이 아닌 채널 (2026-09-28 음원 점검) ──────────────────────────────────
// 가스 문장은 「○○ 누출 → 대피」다. 그런데 산소는 결핍·과잉(「산소 누출」은 반대 뜻),
// 연기는 감지, 온도·습도는 환경 이상이라 누출이 아니다. 특히 매니져가 올린 TEMPERATURE·HUMIDITY
// 경보는 이름표가 없어 영문 코드 그대로 「TEMPERATURE 누출… 즉시 대피」로 읽혔다.
type Abnormal = "o2" | "smoke" | "temp" | "hum";
function abnormalOf(gas?: string | null): Abnormal | null {
  const c = (gas ?? "").trim().toUpperCase();
  if (c === "O2") return "o2";
  if (c === "SMOKE") return "smoke";
  if (c === "TEMPERATURE" || c === "TEMP") return "temp";
  if (c === "HUMIDITY" || c === "HUM") return "hum";
  return null;
}
const ABN_HEAD: Record<AlertLang, [string, string, string, string]> = {
  ko: ["정상.", "주의.", "경계 경보.", "심각 경보."],
  en: ["Normal.", "Caution.", "Warning.", "Critical alert."],
  zh: ["正常。", "注意。", "警戒警报。", "严重警报。"],
  vi: ["Bình thường.", "Chú ý.", "Cảnh báo cảnh giác.", "Cảnh báo nghiêm trọng."],
  th: ["ปกติ", "ระวัง", "เตือนภัย", "วิกฤต"],
  id: ["Normal.", "Perhatian.", "Peringatan waspada.", "Peringatan kritis."],
  mn: ["Хэвийн.", "Анхааруулга.", "Сэрэмжлүүлэг.", "Ноцтой аюул!"],
  ja: ["正常。", "注意。", "警戒警報。", "重大警報。"],
};
const ABN_EVENT: Record<AlertLang, Record<Abnormal, string>> = {
  ko: { o2: "산소 농도 이상", smoke: "연기 감지", temp: "온도 이상", hum: "습도 이상" },
  en: { o2: "Abnormal oxygen level", smoke: "Smoke detected", temp: "Abnormal temperature", hum: "Abnormal humidity" },
  zh: { o2: "氧气浓度异常", smoke: "检测到烟雾", temp: "温度异常", hum: "湿度异常" },
  vi: { o2: "Nồng độ ôxy bất thường", smoke: "Phát hiện khói", temp: "Nhiệt độ bất thường", hum: "Độ ẩm bất thường" },
  th: { o2: "ระดับออกซิเจนผิดปกติ", smoke: "ตรวจพบควัน", temp: "อุณหภูมิผิดปกติ", hum: "ความชื้นผิดปกติ" },
  id: { o2: "Kadar oksigen tidak normal", smoke: "Asap terdeteksi", temp: "Suhu tidak normal", hum: "Kelembapan tidak normal" },
  mn: { o2: "хүчилтөрөгчийн түвшин хэвийн бус", smoke: "утаа илэрлээ", temp: "температур хэвийн бус", hum: "чийгшил хэвийн бус" },
  ja: { o2: "酸素濃度の異常", smoke: "煙を検知", temp: "温度の異常", hum: "湿度の異常" },
};
type AbnAction = "evac" | "stopVent" | "checkSite" | "check";
const ABN_ACTION: Record<AlertLang, Record<AbnAction, string>> = {
  ko: { evac: "즉시 가까운 비상구로 대피하십시오.", stopVent: "작업을 멈추고 환기하십시오.", checkSite: "현장을 즉시 확인하십시오.", check: "주변을 확인하십시오." },
  en: { evac: "Evacuate to the nearest emergency exit immediately.", stopVent: "Stop work and ventilate.", checkSite: "Check the site immediately.", check: "Check your surroundings." },
  zh: { evac: "请立即从最近的紧急出口撤离。", stopVent: "请停止作业并通风。", checkSite: "请立即检查现场。", check: "请检查周围环境。" },
  vi: { evac: "Hãy sơ tán ngay đến lối thoát hiểm gần nhất.", stopVent: "Dừng công việc và thông gió.", checkSite: "Hãy kiểm tra hiện trường ngay.", check: "Hãy kiểm tra xung quanh." },
  th: { evac: "อพยพไปยังทางออกฉุกเฉินที่ใกล้ที่สุดทันที", stopVent: "หยุดงานและระบายอากาศ", checkSite: "ตรวจสอบหน้างานทันที", check: "ตรวจสอบบริเวณโดยรอบ" },
  id: { evac: "Segera evakuasi ke pintu darurat terdekat.", stopVent: "Hentikan pekerjaan dan lakukan ventilasi.", checkSite: "Segera periksa lokasi.", check: "Periksa sekitar Anda." },
  mn: { evac: "Яаралтай хамгийн ойрын онцгой байдлын гарцаар гадагш гарна уу.", stopVent: "Ажлаа зогсоож, агааржуулалтаа шалгана уу.", checkSite: "Талбайг яаралтай шалгана уу.", check: "Эргэн тойрноо шалгана уу." },
  ja: { evac: "直ちに最寄りの非常口へ避難してください。", stopVent: "作業を中止し、換気してください。", checkSite: "直ちに現場を確認してください。", check: "周囲を確認してください。" },
};
/** 등급·채널 → 행동. 산소·연기 심각만 대피, 온습도는 대피 지시 없이 현장 확인. */
function abnAction(kind: Abnormal, level: Level): AbnAction | null {
  if (level <= 0) return null;
  if (level === 1) return "check";
  if (kind === "o2") return level >= 3 ? "evac" : "stopVent";
  if (kind === "smoke") return level >= 3 ? "evac" : "checkSite";
  return "checkSite";
}
function composeAbnormal(l: AlertLang, level: Level, kind: Abnormal, site?: string): string {
  const head = ABN_HEAD[l][level];
  const ev = ABN_EVENT[l][kind];
  const a = abnAction(kind, level);
  const act = a ? ABN_ACTION[l][a] : "";
  switch (l) {
    case "ko": return `${head} ${site ? `${site} ` : ""}${ev}. ${act}`.trim();
    case "en": return `${head} ${ev}${site ? ` at ${site}` : ""}. ${act}`.trim();
    case "zh": return `${head}${site ?? ""}${ev}。${act}`;
    case "vi": return `${head} ${ev}${site ? ` tại ${site}` : ""}. ${act}`.trim();
    case "th": return `${head} ${ev}${site ? `ที่${site}` : ""} ${act}`.trim();
    case "id": return `${head} ${ev}${site ? ` di ${site}` : ""}. ${act}`.trim();
    case "mn": return `${head} ${site ? `${site}-д ` : ""}${ev}. ${act}`.trim();
    case "ja": return `${head}${site ? `${site}で` : ""}${ev}。${act}`;
  }
}

/** 경보 한 건을 소리 내어 읽을 문장 — 종류(가스/설비 무응답)·채널(산소·연기·온습도)·훈련 여부를 반영한다. */
export function buildSpokenAlert(i: SpokenAlertInput): string {
  const l: AlertLang = ALERT_LANGUAGES.includes(i.lang) ? i.lang : "ko";
  const abn = i.kind === "health" ? null : abnormalOf(i.gas);
  const subj = i.kind === "health" ? healthSubject(i.subjectKey) : null;
  const body =
    subj === "sensor"
      ? composeSensorCheck(l, i.subjectKey, i.site ?? undefined)
      : subj === "lamp"
      ? (LAMP_COMPOSE[l] ?? LAMP_COMPOSE.en)(i.site ?? undefined)
      : subj
      ? HEALTH_COMPOSE[l](subj, i.site ?? undefined)
      : abn
        ? composeAbnormal(l, i.level, abn, i.site ?? undefined)
        : buildAlertPhrase(i);
  return i.isDrill ? `${DRILL_PREFIX[l]} ${body}` : body;
}

/**
 * 경보 제목에 쓰는 대상 이름 — 가스면 가스 이름, 설비 무응답이면 「설비 무응답」,
 * 센서 판독 모순(subject 「sensor:…」)이면 「센서 점검 필요」(subjectKey 를 줄 때만 — 안 주면 종전 그대로).
 */
export function alertSubjectName(
  lang: AlertLang,
  kind?: AlertKindLike | null,
  gas?: string | null,
  subjectKey?: string | null,
): string {
  const l: AlertLang = ALERT_LANGUAGES.includes(lang) ? lang : "ko";
  if (kind === "health") {
    const s = healthSubject(subjectKey);
    if (s === "sensor") return SENSOR_CHECK_WORD[l] ?? SENSOR_CHECK_WORD.en;
    if (s === "lamp") return LAMP_WORD[l] ?? LAMP_WORD.en;
    return HEALTH_WORD[l];
  }
  const abn = abnormalOf(gas);
  if (abn === "temp" || abn === "hum") return ABN_EVENT[l][abn];   // 영문 코드(TEMPERATURE) 대신
  return gasDisplayName(l, gas);
}

// ── 해제 안내 (2026-09-28 음원 점검: 「해제·복구 시 음성 없음」) ─────────────────────────────
// 낭독한 경보가 해제되면 **주 음성 언어로 한 번** 알린다. 대피를 부른 방송만 있고 해제 방송이 없으면
// 현장·관제는 「아직 위험한가」를 화면을 열어 봐야 안다. 전 언어로 되풀이하지 않는다 — 해제는 행동
// 지시가 아니라 상태 안내라서, 8개 언어 순차(수십 초)가 다음 실경보 낭독을 밀어내면 안 된다.
const CLEARED_COMPOSE: Record<AlertLang, (subject: string, site?: string) => string> = {
  ko: (s, site) => `${site ? `${site} ` : ""}${s} 경보가 해제되었습니다.`,
  en: (s, site) => `The ${s} alert${site ? ` at ${site}` : ""} has been cleared.`,
  zh: (s, site) => `${site ?? ""}${s}警报已解除。`,
  vi: (s, site) => `Cảnh báo ${s}${site ? ` tại ${site}` : ""} đã kết thúc.`,
  th: (s, site) => `การแจ้งเตือน${s}${site ? `ที่${site}` : ""}สิ้นสุดแล้ว`,
  id: (s, site) => `Peringatan ${s}${site ? ` di ${site}` : ""} telah berakhir.`,
  mn: (s, site) => `${site ? `${site}-ийн ` : ""}${s} дохиолол дууслаа.`,
  ja: (s, site) => `${site ? `${site}の` : ""}${s}警報は解除されました。`,
};

/** 낭독했던 경보가 해제됐을 때의 안내 문장 — 종류(가스/설비 무응답)·채널 이름·훈련 표시를 반영. */
export function buildClearedAlert(i: SpokenAlertInput): string {
  const l: AlertLang = ALERT_LANGUAGES.includes(i.lang) ? i.lang : "ko";
  const body = CLEARED_COMPOSE[l](alertSubjectName(l, i.kind, i.gas, i.subjectKey), i.site ?? undefined);
  return i.isDrill ? `${DRILL_PREFIX[l]} ${body}` : body;
}
